import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useMutation, useQuery } from "convex/react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../Context/LanguageContext";
import { FiArrowLeft, FiClock, FiEdit2 } from "react-icons/fi";
import { api } from "../convexApi";
import BlogUnavailable from "../Components/BlogUnavailable";
import BlogContentRenderer from "../Components/BlogContentRenderer";
import { isBlogBackendConfigured, hasStoredAdminKey } from "../services/blogService";
import "./blog.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const BlogDetailConnected = () => {
  const { t, language } = useLanguage();
  const { id } = useParams();
  const post = useQuery(api.blog.getById, id ? { id } : "skip");
  const comments = useQuery(api.blog.commentsByPost, id ? { postId: id } : "skip");
  const addComment = useMutation(api.blog.addComment);
  const [authorName, setAuthorName] = useState("");
  const [content, setContent] = useState("");
  const isAdmin = hasStoredAdminKey();

  const submitComment = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error(t("EnterComment"));
      return;
    }

    try {
      await addComment({
        postId: id,
        authorName: authorName.trim() || t("Anonymous"),
        content,
      });
      setContent("");
      toast.success(t("CommentAdded"));
    } catch (error) {
      toast.error(error.message || t("Error"));
    }
  };

  if (post === undefined) {
    return (
      <motion.div
        className="blog-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        key={language}
      >
        <div className="blog-page__inner">
          <div className="blog-empty mono">
            <p>{t("LoadingPost")}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!post) {
    return (
      <motion.div
        className="blog-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        key={language}
      >
        <div className="blog-page__inner">
          <div className="blog-empty mono">
            <p>{t("PostNotFound")}</p>
            <Link to="/blog" className="blog-primary-btn">
              <FiArrowLeft size={14} /> {t("BackToBlog")}
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="blog-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      <div className="blog-page__inner">
        <div className="blog-detail__top">
          <Link to="/blog" className="blog-secondary-btn mono">
            <FiArrowLeft size={14} /> {t("Back")}
          </Link>
          {isAdmin ? (
            <Link to={`/blog/edit/${post.id}`} className="blog-secondary-btn mono">
              <FiEdit2 size={14} /> {t("EditPost")}
            </Link>
          ) : null}
        </div>

        <article className="blog-detail">
          {post.image ? (
            <img src={post.image} alt={post.title} className="blog-detail__image" />
          ) : null}
          <h1 className="blog-detail__title">{post.title}</h1>
          <div className="blog-detail__meta mono">
            <span>
              <FiClock size={12} /> {post.readTime}
            </span>
            <span>{new Date(post.createdAt).toLocaleDateString()}</span>
          </div>

          <p className="blog-detail__excerpt">{post.excerpt}</p>

          <div className="blog-card__tags">
            {post.tags.map((tag) => (
              <span key={tag} className="blog-card__tag mono">
                {tag}
              </span>
            ))}
          </div>

          <BlogContentRenderer content={post.content} postId={post.id} />
        </article>

        <section className="blog-comments">
          <h2 className="blog-comments__title">{t("Comments")}</h2>
          <form className="blog-comments__form" onSubmit={submitComment}>
            <input
              className="blog-form__input"
              placeholder={t("YourName")}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
            />
            <textarea
              className="blog-form__textarea"
              rows={4}
              placeholder={t("WriteComment")}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <button type="submit" className="blog-primary-btn mono">
              {t("AddComment")}
            </button>
          </form>

          <div className="blog-comments__list">
            {comments === undefined ? (
              <p className="mono">{t("LoadingComments")}</p>
            ) : comments.length === 0 ? (
              <p className="mono">{t("NoComments")}</p>
            ) : (
              comments.map((comment) => (
                <article key={comment._id} className="blog-comment-card">
                  <div className="blog-comment-card__meta mono">
                    <strong>{comment.authorName}</strong>
                    <span>{new Date(comment.createdAt).toLocaleString()}</span>
                  </div>
                  <p>{comment.content}</p>
                </article>
              ))
            )}
          </div>
        </section>
      </div>
    </motion.div>
  );
};

const BlogDetail = () => {
  if (!isBlogBackendConfigured) {
    return <BlogUnavailable />;
  }

  return <BlogDetailConnected />;
};

export default BlogDetail;
